import { c as _c } from "react/compiler-runtime";
import React from "react";
import { ImageWithPreview, SanityImage, buildSrc } from "sanity-image";
import type { SanityAsset, SanityImageObject } from "@sanity/image-url";
import { projectConfig } from "../../lib/config";
import { buildResponsiveSrcSet } from "../../util/responsiveImageSrcSet";
const DEFAULT_SIZES = "(min-width: 1521px) 760px, (min-width: 1181px) 640px, 90vw";
export const ResponsiveImage: React.FC<{
  image: SanityImageObject;
  alt?: string;
  onClick?: () => void;
  priority?: boolean;
  /** Must match the rendered CSS width of the image per breakpoint, or retina screens get blurry images. */
  sizes?: string;
  layout?: "fill" | "responsive" | "cover" | "contain";
  /** For layout="cover": the narrowest width / height ratio of the box the image covers. */
  coverAspectRatio?: number;
}> = t0 => {
  const $ = _c(35);
  const {
    image,
    alt,
    onClick,
    priority,
    sizes,
    layout: t1,
    coverAspectRatio
  } = t0;
  const layout = t1 === undefined ? "fill" : t1;
  if (!image) {
    return null;
  }
  if (!image.asset) {
    return null;
  }
  const imageId = image.asset?._ref || (image.asset as SanityAsset)._id;
  if (!imageId) {
    return null;
  }
  const {
    projectId,
    dataset
  } = projectConfig;
  const baseUrl = `https://cdn.sanity.io/images/${projectId}/${dataset}/`;
  const preview = (image as any).asset?.metadata?.lqip;
  const dimensions = (image as any).asset?.metadata?.dimensions;
  const aspectRatio = dimensions ? dimensions.width / dimensions.height : undefined;
  const mode = layout === "cover" ? "cover" : "contain";
  const t2 = layout === "fill" || layout === "cover" ? "100%" : "auto";
  let t3;
  if ($[0] !== mode || $[1] !== t2) {
    t3 = {
      objectFit: mode,
      width: "100%",
      height: t2
    };
    $[0] = mode;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const imageStyle = t3;
  let img;
  let isResponsive;
  if ($[3] !== alt || $[4] !== aspectRatio || $[5] !== coverAspectRatio || $[6] !== image.crop || $[7] !== image.hotspot || $[8] !== imageId || $[9] !== imageStyle || $[10] !== layout || $[11] !== mode || $[12] !== onClick || $[13] !== preview || $[14] !== priority || $[15] !== sizes) {
    const dimensionProps = {};
    if (layout !== "fill" && layout !== "responsive") {
      dimensionProps.width = 800;
    }
    isResponsive = layout === "responsive" && aspectRatio;
    let t4;
    if ($[18] !== imageStyle || $[19] !== isResponsive) {
      t4 = isResponsive ? {
        ...imageStyle,
        height: "100%",
        display: "block"
      } : imageStyle;
      $[18] = imageStyle;
      $[19] = isResponsive;
      $[20] = t4;
    } else {
      t4 = $[20];
    }
    const style = t4;
    const imageParams = {
      baseUrl,
      id: imageId,
      mode,
      hotspot: image.hotspot,
      crop: image.crop,
      queryParams: {
        q: 80
      }
    } as const;
    const t5 = alt || "Image";
    const t6 = priority ? "eager" as const : "lazy" as const;
    const t7 = sizes ?? DEFAULT_SIZES;
    let t8;
    if ($[21] !== onClick || $[22] !== style || $[23] !== t5 || $[24] !== t6 || $[25] !== t7) {
      t8 = {
        alt: t5,
        onClick,
        loading: t6,
        sizes: t7,
        style
      };
      $[21] = onClick;
      $[22] = style;
      $[23] = t5;
      $[24] = t6;
      $[25] = t7;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    const imageProps = t8;
    if (imageId.endsWith("-svg")) {
      img = <SanityImage id={imageId} baseUrl={baseUrl} mode={mode} preview={preview} hotspot={image.hotspot} crop={image.crop} {...imageProps} {...dimensionProps} />;
    } else {
      const {
        src,
        width,
        height
      } = buildSrc({
        ...imageParams,
        ...dimensionProps
      });
      const attributes = {
        ...imageProps,
        srcSet: buildResponsiveSrcSet(imageParams, layout === "cover" ? coverAspectRatio : undefined),
        src,
        width,
        height
      };
      let t9;
      if ($[27] !== attributes || $[28] !== preview) {
        t9 = preview ? <ImageWithPreview as="img" preview={preview} {...attributes} /> : <img {...attributes} alt={attributes.alt} />;
        $[27] = attributes;
        $[28] = preview;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      img = t9;
    }
    $[3] = alt;
    $[4] = aspectRatio;
    $[5] = coverAspectRatio;
    $[6] = image.crop;
    $[7] = image.hotspot;
    $[8] = imageId;
    $[9] = imageStyle;
    $[10] = layout;
    $[11] = mode;
    $[12] = onClick;
    $[13] = preview;
    $[14] = priority;
    $[15] = sizes;
    $[16] = img;
    $[17] = isResponsive;
  } else {
    img = $[16];
    isResponsive = $[17];
  }
  if (isResponsive) {
    const t4 = `${aspectRatio}`;
    let t5;
    if ($[30] !== t4) {
      t5 = {
        width: "100%",
        aspectRatio: t4,
        display: "block"
      };
      $[30] = t4;
      $[31] = t5;
    } else {
      t5 = $[31];
    }
    const containerStyle = t5;
    let t6;
    if ($[32] !== containerStyle || $[33] !== img) {
      t6 = <div style={containerStyle}>{img}</div>;
      $[32] = containerStyle;
      $[33] = img;
      $[34] = t6;
    } else {
      t6 = $[34];
    }
    return t6;
  }
  return img;
};
export default ResponsiveImage;